/**
 * canvas-engine/index.js
 *
 * Public Canvas Engine exports — the single import surface for both the
 * Console builder (admin-console-ui) and the published reader (academy-ui).
 *
 * ZERO-REUSE CONSTRAINT: the entire `canvas-engine/` module imports NOTHING
 * from `admin-console-ui/src/modules/template-builder/` or
 * `academy-ui/src/template/`. It is a self-contained, single source of truth.
 *
 * NOTE: This is the Task 1.1 scaffold. Re-exports are added incrementally as
 * later tasks land their modules (canvasModel in 1.2, css/* in 2.1, presets/*
 * in 3.x, migration in 4.1, renderer/* in 6.x). For now it surfaces the schema
 * constants and the asset-reference helpers.
 */

// Schema constants (own). (Task 1.1, Req 19.1, 21.1)
export {
  CANVAS_SCHEMA_VERSION,
  REFERENCE_WIDTH,
  DEFAULT_FRAME_HEIGHT,
  LAYOUT_MODE_ABSOLUTE,
  ELEMENT_TYPES,
  CANVAS_SECTION_TYPE,
  MIN_DIMENSION,
  MIN_OPACITY,
  MAX_OPACITY,
} from './model/schema';

// Asset reference normalization (own). (Task 1.1, Req 22.4)
export { normalizeAsset, assetRef, isEmptyAsset, EMPTY_ASSET } from './model/assetRef';

// Presigned-URL-at-display-time plumbing (own): durable stored keys are
// persisted; a fresh display URL is resolved at render via an app-injected
// resolver. Never persists a pre-signed URL.
export {
  AssetUrlResolverContext,
  AssetUrlResolverProvider,
  usePresignedSrc,
  EngineImage,
} from './model/assetUrlResolver';

// Canvas model primitives (own). (Task 1.2, Req 19.1, 22.3, 22.4, 24.1, 24.2)
export {
  CANVAS_SCHEMA_VERSION as CANVAS_MODEL_SCHEMA_VERSION,
  ELEMENT_DEFAULTS,
  FRAME_DEFAULTS,
  ASSET_CONTENT_KEYS,
  defaultElement,
  ensureElementDefaults,
  normalizeCanvas,
  validateCanvas,
  isCanvasModel,
  applyCanvasPatch,
} from './model/canvasModel';

// Canvas_Preset registry + instantiation (own). (Task 3.4, Req 4.5, 4.6, 5.7, 9.2)
export {
  CANVAS_PRESETS,
  getPreset,
  listPresets,
  instantiatePreset,
  createSectionFromPreset,
} from './presets/canvasPresets';

// Default-template scaffolding (own): per-domain default pages + sections so a
// fresh draft opens pre-filled with the domain's default look.
export {
  normalizeDomainType,
  DOMAIN_PAGE_BLUEPRINTS,
  getDomainPageBlueprint,
  createDefaultTemplate,
} from './presets/defaultTemplates';

// Load-time Section_Migration (own). (Task 4.1, Req 6.1, 6.2, 6.3, 6.4, 6.5)
export {
  isCanvasSection,
  migrateSectionToCanvas,
  migrateTemplateToCanvas,
} from './migration/sectionMigration';

// Unified renderer + section error boundary (own). (Task 6.2, Req 2.6, 20.1,
// 20.5, 20.6, 20.7, 24.1, 24.2, 24.3)
export { default as CanvasRenderer } from './renderer/CanvasRenderer';
export { default as CanvasPage } from './renderer/CanvasPage';
export { default as CanvasSectionErrorBoundary } from './renderer/CanvasSectionErrorBoundary';
